import { FabricObject } from 'fabric';
import { ObjectTypeEnum } from '../../_enums/object-type.enum';
import { buildMnemonicImageUrl, stripAuthTokenFromUrl } from './fabric-image-url.builder';
import { MnemonicCanvasSaveOptions } from './fabric-canvas.types';

function serializeGaugeConfig(gauge: any): Record<string, unknown> | undefined {
    if (!gauge) {
        return undefined;
    }

    return { ...gauge };
}

function serializeChartConfig(chart: any): Record<string, unknown> | undefined {
    if (!chart) {
        return undefined;
    }

    return {
        ...chart,
        data: chart.data
            ? {
                  ...chart.data,
                  datasets: chart.data.datasets?.map((dataset: any) => ({
                      data: [...(dataset.data ?? [])],
                      label: dataset.label,
                  })),
              }
            : chart.data,
    };
}

function findCanvasObjectForSerializedItem(item: Record<string, unknown>, canvasObjects: FabricObject[], index: number): any {
    if (item.id) {
        const matched = canvasObjects.find((object: any) => object.id === item.id);
        if (matched) {
            return matched;
        }
    }

    return canvasObjects[index] ?? null;
}

function getImageSrc(object: any): string | undefined {
    if (typeof object?.getSrc === 'function') {
        return object.getSrc();
    }

    return object?.src;
}

export function resolveMnemonicImageId(object: Record<string, unknown> | any): string | undefined {
    return object?.imageFileId ?? object?.id;
}

function resolveImageSrcForSave(currentObj: any, mnemonicImages?: Array<{ id: string; fileName: string }>): string | undefined {
    const imageId = resolveMnemonicImageId(currentObj);
    const mnemonicImage = mnemonicImages?.find((image) => image.id === imageId);

    if (mnemonicImage?.fileName) {
        return buildMnemonicImageUrl(mnemonicImage.fileName);
    }

    const src = getImageSrc(currentObj);

    if (typeof src === 'string' && src.includes('/api/virtual-devices/mnemonic-file/')) {
        return stripAuthTokenFromUrl(src);
    }

    return src;
}

export function isSerializedImageType(item: Record<string, unknown>, currentObj?: any): boolean {
    const fabricType = String(item.type ?? currentObj?.type ?? '').toLowerCase();
    const objectType = item.objectType ?? currentObj?.objectType;

    return fabricType === ObjectTypeEnum.image || objectType === ObjectTypeEnum.image || objectType === ObjectTypeEnum.defaultImage;
}

function enrichMnemonicBackgroundImageForSave(
    serializedBackground: Record<string, unknown> | undefined,
    backgroundImage: FabricObject | null | undefined,
    mnemonicImages?: Array<{ id: string; fileName: string }>,
): Record<string, unknown> | undefined {
    if (!serializedBackground && !backgroundImage) {
        return undefined;
    }

    const currentObj = backgroundImage as any;
    const base = serializedBackground ?? { type: ObjectTypeEnum.image };

    if (!currentObj) {
        return serializedBackground;
    }

    return {
        ...base,
        type: ObjectTypeEnum.image,
        id: resolveMnemonicImageId(currentObj),
        imageFileId: resolveMnemonicImageId(currentObj),
        src: resolveImageSrcForSave(currentObj, mnemonicImages),
        objectType: currentObj.objectType ?? ObjectTypeEnum.image,
        name: currentObj.name,
        text: currentObj.text,
        tooltip: currentObj.tooltip,
    };
}

function isGaugeObject(item: Record<string, unknown>, currentObj: any): boolean {
    return currentObj?.objectType === ObjectTypeEnum.gauge || item.type === ObjectTypeEnum.gauge;
}

function isChartObject(item: Record<string, unknown>, currentObj: any): boolean {
    return currentObj?.objectType === ObjectTypeEnum.chart || item.type === ObjectTypeEnum.chart;
}

export function enrichMnemonicObjectsForSave(
    items: Record<string, unknown>[],
    canvasObjects: FabricObject[],
    mnemonicImages?: Array<{ id: string; fileName: string }>,
): Record<string, unknown>[] {
    return items.map((item, index) => {
        const currentObj = findCanvasObjectForSerializedItem(item, canvasObjects, index);

        if (!currentObj) {
            return item;
        }

        if (currentObj.objectType === ObjectTypeEnum.path && item.type === ObjectTypeEnum.group) {
            return {
                ...item,
                src: currentObj.src,
                deviceId: currentObj.deviceId,
                variableId: currentObj.variableId,
                selectOption: currentObj.selectOption,
                objectType: currentObj.objectType,
                tooltip: currentObj.tooltip,
            };
        }

        if (isGaugeObject(item, currentObj)) {
            return {
                ...item,
                type: ObjectTypeEnum.gauge,
                id: currentObj.id,
                name: currentObj.name,
                objectType: ObjectTypeEnum.gauge,
                tooltip: currentObj.tooltip,
                deviceId: currentObj.deviceId,
                variableId: currentObj.variableId,
                selectOption: currentObj.selectOption,
                gauge: serializeGaugeConfig(currentObj.gauge),
            };
        }

        if (isChartObject(item, currentObj)) {
            return {
                ...item,
                type: ObjectTypeEnum.chart,
                id: currentObj.id,
                name: currentObj.name,
                objectType: ObjectTypeEnum.chart,
                tooltip: currentObj.tooltip,
                deviceId: currentObj.deviceId,
                variableId: currentObj.variableId,
                selectOption: currentObj.selectOption,
                chart: serializeChartConfig(currentObj.chart),
            };
        }

        if (isSerializedImageType(item, currentObj)) {
            const imageId = resolveMnemonicImageId(currentObj);
            const mnemonicImage = mnemonicImages?.find((image) => image.id === imageId);

            return {
                ...item,
                type: ObjectTypeEnum.image,
                id: imageId,
                imageFileId: imageId,
                src: resolveImageSrcForSave(currentObj, mnemonicImages),
                objectType: mnemonicImage ? ObjectTypeEnum.image : currentObj.objectType,
                name: currentObj.name,
                text: currentObj.text,
                tooltip: currentObj.tooltip,
            };
        }

        if (currentObj.objectType === ObjectTypeEnum.group) {
            return {
                ...item,
                objectType: currentObj.objectType,
                name: currentObj.name,
                text: currentObj.text,
                tooltip: currentObj.tooltip,
            };
        }

        if (!currentObj.objectType) {
            return item;
        }

        return {
            ...item,
            id: currentObj.id,
            name: currentObj.name,
            objectType: currentObj.objectType,
            tooltip: currentObj.tooltip,
            deviceId: currentObj.deviceId,
            variableId: currentObj.variableId,
            selectOption: currentObj.selectOption,
            text: currentObj.text,
            isStatusColor: currentObj.isStatusColor,
        };
    });
}

export function enrichMnemonicCanvasForSave(
    json: Record<string, unknown>,
    canvasObjects: FabricObject[],
    backgroundImage: FabricObject | null | undefined,
    options: MnemonicCanvasSaveOptions = {},
): Record<string, unknown> {
    return {
        ...json,
        objects: enrichMnemonicObjectsForSave((json.objects as Record<string, unknown>[]) ?? [], canvasObjects, options.mnemonicImages),
        backgroundImage: enrichMnemonicBackgroundImageForSave(json.backgroundImage as Record<string, unknown> | undefined, backgroundImage, options.mnemonicImages),
    };
}
